<script lang="ts">
  import PencilSimpleIcon from 'phosphor-svelte/lib/PencilSimpleIcon';
  import PlusIcon from 'phosphor-svelte/lib/PlusIcon';
  import TrashIcon from 'phosphor-svelte/lib/TrashIcon';
  import ArrowDownIcon from 'phosphor-svelte/lib/ArrowDownIcon';
  import ArrowUpIcon from 'phosphor-svelte/lib/ArrowUpIcon';

  import type { PersonaView } from '#src/generated/protocol';

  import { useCoreClient } from '#lib/core/context.js';
  import { pickFiles, saveBytes } from '#lib/platform/files.js';
  import { importPersonaAvatar } from '#lib/platform/persona-avatar.js';
  import { i18n } from '#lib/i18n.js';
  import { backupFileName } from '#lib/personas/backup.js';
  import { usePersonaStore } from '#lib/personas/personas.svelte.js';
  import {
    fetchPluralkitMembers,
    matchImported,
    parsePluralkitExport,
    personaFromPluralkit,
    systemIdFromInput,
    type PluralkitImportOptions,
    type PluralkitMember,
  } from '#lib/personas/pluralkit.js';
  import { triggerLabel } from '#lib/personas/persona.js';
  import { formatPronouns } from '#lib/personas/pronouns.js';
  import Alert from '#lib/ui/primitives/Alert.svelte';
  import Avatar from '#lib/ui/primitives/Avatar.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import ConfirmDialog from '#lib/ui/primitives/ConfirmDialog.svelte';
  import IconButton from '#lib/ui/primitives/IconButton.svelte';
  import SettingsSection from '#lib/ui/primitives/SettingsSection.svelte';
  import Spinner from '#lib/ui/primitives/Spinner.svelte';
  import TextInput from '#lib/ui/primitives/TextInput.svelte';
  import Switch from '#lib/ui/primitives/Switch.svelte';
  import '#lib/ui/primitives/settings-row.css';

  import PersonaEditor from './PersonaEditor.svelte';

  const core = useCoreClient();
  const personas = usePersonaStore();

  let editorOpen = $state(false);
  let editing = $state<PersonaView | null>(null);
  let removing = $state<string | null>(null);
  let personaToRemove = $state<PersonaView | null>(null);
  let confirmingRemoveAll = $state(false);
  let removingAll = $state(false);
  let error = $state<string | null>(null);

  let systemInput = $state('');
  let token = $state('');
  let pkExtractPronouns = $state(true);
  let importing = $state(false);
  let importNotice = $state<string | null>(null);
  let exportInput = $state<HTMLInputElement>();

  let backingUp = $state(false);
  let backupInput = $state<HTMLInputElement>();
  let restoreContent = $state.raw<string | null>(null);
  let restoring = $state(false);

  $effect(() => {
    void personas.load();
  });

  function openEditor(persona: PersonaView | null): void {
    editing = persona;
    editorOpen = true;
  }

  async function save(
    persona: PersonaView,
    previousId: string | null,
    basePersona: PersonaView | null
  ): Promise<void> {
    await personas.save(persona, previousId, basePersona);
    error = null;
  }

  async function remove(persona: PersonaView): Promise<void> {
    removing = persona.id;
    error = null;
    try {
      await personas.remove(persona.id);
    } catch (cause) {
      console.warn('[sable personas] deleting failed', cause);
      error = i18n.t('personas.removeFailed');
    } finally {
      removing = null;
    }
  }

  async function confirmRemoval(): Promise<void> {
    const persona = personaToRemove;
    if (!persona) return;
    await remove(persona);
    personaToRemove = null;
  }

  async function confirmRemoveAll(): Promise<void> {
    removingAll = true;
    error = null;
    try {
      await personas.removeAll();
    } catch (cause) {
      console.warn('[sable personas] deleting every profile failed', cause);
      error = i18n.t('personas.removeFailed');
    } finally {
      removingAll = false;
      confirmingRemoveAll = false;
    }
  }

  async function avatarFor(member: PluralkitMember, existing: PersonaView | undefined) {
    if (!member.avatar_url) return { url: existing?.avatar_url ?? null, failed: false };
    if (existing?.pluralkit?.avatar_url === member.avatar_url && existing.avatar_url) {
      return { url: existing.avatar_url, failed: false };
    }

    try {
      return { url: await importPersonaAvatar(member.avatar_url, core.media), failed: false };
    } catch (cause) {
      console.warn('[sable personas] fetching a PluralKit picture failed', cause);
      return { url: existing?.avatar_url ?? null, failed: true };
    }
  }

  async function importMembers(
    load: () => Promise<PluralkitMember[]>,
    failure: string,
    options: PluralkitImportOptions = {}
  ): Promise<boolean> {
    importing = true;
    error = null;
    importNotice = null;
    try {
      const members = await load();
      let pictures = 0;

      for (const member of members) {
        const existing = matchImported(personas.personas, member);
        const avatar = await avatarFor(member, existing);
        if (avatar.failed) pictures += 1;
        await personas.save(
          personaFromPluralkit(existing?.id ?? crypto.randomUUID(), member, avatar.url, options),
          existing?.id ?? null
        );
      }

      importNotice =
        pictures === 0
          ? i18n.t('personas.importDone', { count: members.length })
          : i18n.t('personas.importAvatarFailed', { count: pictures });
      return true;
    } catch (cause) {
      console.warn('[sable personas] the PluralKit import failed', cause);
      error = i18n.t(failure);
      return false;
    } finally {
      importing = false;
    }
  }

  async function runImport(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    const systemId = systemIdFromInput(systemInput);
    if (systemId === '') return;

    const imported = await importMembers(
      () => fetchPluralkitMembers(systemId, token.trim() || null),
      'personas.importFailed',
      { extractPronouns: pkExtractPronouns }
    );
    if (imported) token = '';
  }

  async function importExport(files: File[]): Promise<void> {
    const [file] = files;
    if (!file || importing) return;
    await importMembers(
      async () => parsePluralkitExport(await file.text()),
      'personas.importFileFailed',
      { extractPronouns: pkExtractPronouns }
    );
  }

  async function chooseExport(): Promise<void> {
    const picked = await pickFiles('*/*');
    if (picked === null) exportInput?.click();
    else await importExport(picked);
  }

  async function backUp(): Promise<void> {
    backingUp = true;
    error = null;
    try {
      const bytes = new TextEncoder().encode(await core.rpc.personas.backup());
      if ((await saveBytes(bytes, backupFileName(), 'application/json')) === 'failed') {
        error = i18n.t('personas.backupFailed');
      }
    } catch (cause) {
      console.warn('[sable personas] the backup failed', cause);
      error = i18n.t('personas.backupFailed');
    } finally {
      backingUp = false;
    }
  }

  async function readBackup(files: File[]): Promise<void> {
    const [file] = files;
    if (!file) return;
    error = null;
    try {
      const backup = await file.text();
      await core.rpc.personas.checkBackup({ backup });
      restoreContent = backup;
    } catch (cause) {
      console.warn('[sable personas] the backup file could not be read', cause);
      error = i18n.t('personas.restoreInvalid');
    }
  }

  async function chooseBackup(): Promise<void> {
    const picked = await pickFiles('*/*');
    if (picked === null) backupInput?.click();
    else await readBackup(picked);
  }

  async function confirmRestore(): Promise<void> {
    const backup = restoreContent;
    if (backup === null) return;
    restoring = true;
    try {
      await core.rpc.personas.restoreBackup({ backup });
      await personas.load(true);
    } catch (cause) {
      console.warn('[sable personas] the restore failed', cause);
      error = i18n.t('personas.restoreFailed');
    } finally {
      restoring = false;
      restoreContent = null;
    }
  }
</script>

<div class="persona-stack">
  {#if error}<Alert variant="critical" aria-live="polite">{error}</Alert>{/if}
  {#if personas.error}<Alert variant="critical">{i18n.t(personas.error)}</Alert>{/if}

  <SettingsSection
    title={i18n.t('personas.manageTitle')}
    description={i18n.t('personas.manageDescription')}
    headingId="settings-personas"
  >
    <div class="settings-form">
      {#if personas.loading && personas.personas.length === 0}
        <p class="persona-empty"><Spinner small label={i18n.t('a11y.loading')} /></p>
      {:else if personas.personas.length === 0}
        <p class="persona-empty">{i18n.t('personas.empty')}</p>
      {:else}
        <ul class="persona-list">
          {#each personas.personas as persona (persona.id)}
            {@const index = personas.personas.indexOf(persona)}
            <li>
              <Avatar
                id={persona.id}
                src={persona.avatar_url}
                name={persona.display_name}
                size="small"
              />
              <div class="persona-copy">
                <span class="persona-name">{persona.display_name}</span>
                {#if persona.pronouns.length > 0}
                  <span class="persona-meta">{formatPronouns(persona.pronouns)}</span>
                {/if}
                {#if persona.triggers.length > 0}
                  <span class="persona-meta">
                    {persona.triggers.map(triggerLabel).join(' · ')}
                  </span>
                {/if}
              </div>
              <IconButton
                variant="subtle"
                size="small"
                label={i18n.t('personas.edit', { name: persona.display_name })}
                onclick={() => {
                  openEditor(persona);
                }}
              >
                <PencilSimpleIcon />
              </IconButton>
              <IconButton
                variant="subtle"
                size="small"
                disabled={index === 0}
                label={i18n.t('personas.moveUp', { name: persona.display_name })}
                onclick={() => void personas.reorder(index, index - 1)}
              >
                <ArrowUpIcon />
              </IconButton>
              <IconButton
                variant="subtle"
                size="small"
                disabled={index === personas.personas.length - 1}
                label={i18n.t('personas.moveDown', { name: persona.display_name })}
                onclick={() => void personas.reorder(index, index + 1)}
              >
                <ArrowDownIcon />
              </IconButton>
              <IconButton
                variant="subtle"
                size="small"
                disabled={removing === persona.id}
                label={i18n.t('personas.remove', { name: persona.display_name })}
                onclick={() => {
                  personaToRemove = persona;
                }}
              >
                <TrashIcon />
              </IconButton>
            </li>
          {/each}
        </ul>
      {/if}

      <div class="persona-actions">
        <Button
          variant="secondary"
          size="small"
          disabled={personas.personas.length === 0 || importing || restoring}
          onclick={() => {
            confirmingRemoveAll = true;
          }}
        >
          <TrashIcon />
          {i18n.t('personas.removeAll')}
        </Button>
        <Button
          variant="secondary"
          size="small"
          onclick={() => {
            openEditor(null);
          }}
        >
          <PlusIcon />
          {i18n.t('personas.add')}
        </Button>
      </div>
    </div>
  </SettingsSection>

  <SettingsSection
    title={i18n.t('personas.importTitle')}
    description={i18n.t('personas.importDescription')}
    headingId="settings-personas-pluralkit"
  >
    <form class="settings-form import-form" onsubmit={(event) => void runImport(event)}>
      {#if importNotice}<Alert variant="info" aria-live="polite">{importNotice}</Alert>{/if}
      <label class="field">
        <span>{i18n.t('personas.importSystem')}</span>
        <TextInput
          bind:value={systemInput}
          required
          placeholder={i18n.t('personas.importSystemPlaceholder')}
        />
      </label>
      <label class="field">
        <span>{i18n.t('personas.importToken')}</span>
        <TextInput bind:value={token} type="password" autocomplete="off" />
        <small>{i18n.t('personas.importTokenHint')}</small>
      </label>
      <label class="field">
        <div>
          <span>{i18n.t('personas.importPronouns')}</span>
          <Switch label={i18n.t('personas.importPronouns')} bind:checked={pkExtractPronouns} />
        </div>
      </label>
      <div class="persona-actions">
        <Button
          variant="secondary"
          size="small"
          disabled={importing}
          onclick={() => void chooseExport()}
        >
          {i18n.t('personas.importFile')}
        </Button>
        <Button type="submit" size="small" loading={importing}>
          {i18n.t('common.import')}
        </Button>
      </div>
      <input
        bind:this={exportInput}
        class="screen-reader-only"
        type="file"
        accept=".json,application/json"
        tabindex="-1"
        aria-hidden="true"
        onchange={(event) => {
          const files = [...(event.currentTarget.files ?? [])];
          event.currentTarget.value = '';
          void importExport(files);
        }}
      />
    </form>
  </SettingsSection>

  <SettingsSection
    title={i18n.t('personas.backupTitle')}
    description={i18n.t('personas.backupDescription')}
    headingId="settings-personas-backup"
  >
    <div class="settings-form">
      <div class="persona-actions">
        <Button
          variant="secondary"
          size="small"
          disabled={importing || restoring}
          onclick={() => void chooseBackup()}
        >
          {i18n.t('common.restore')}
        </Button>
        <Button
          variant="secondary"
          size="small"
          loading={backingUp}
          disabled={personas.personas.length === 0}
          onclick={() => void backUp()}
        >
          {i18n.t('personas.backup')}
        </Button>
      </div>
      <input
        bind:this={backupInput}
        class="screen-reader-only"
        type="file"
        accept=".json,application/json"
        tabindex="-1"
        aria-hidden="true"
        onchange={(event) => {
          const files = [...(event.currentTarget.files ?? [])];
          event.currentTarget.value = '';
          void readBackup(files);
        }}
      />
    </div>
  </SettingsSection>
</div>

<PersonaEditor
  bind:open={editorOpen}
  persona={editing}
  onSave={save}
  onOpenChange={(next: boolean) => {
    editorOpen = next;
    if (!next) editing = null;
  }}
/>

<ConfirmDialog
  open={personaToRemove !== null}
  onOpenChange={(next: boolean) => {
    if (!next && removing === null) personaToRemove = null;
  }}
  title={i18n.t('personas.removeTitle', { name: personaToRemove?.display_name ?? '' })}
  description={i18n.t('personas.removeConfirm')}
  confirmLabel={i18n.t('personas.remove', { name: personaToRemove?.display_name ?? '' })}
  busy={removing !== null}
  onConfirm={() => void confirmRemoval()}
/>

<ConfirmDialog
  open={confirmingRemoveAll}
  onOpenChange={(next: boolean) => {
    if (!next && !removingAll) confirmingRemoveAll = false;
  }}
  title={i18n.t('personas.removeAllTitle')}
  description={i18n.t('personas.removeAllConfirm')}
  confirmLabel={i18n.t('personas.removeAll')}
  busy={removingAll}
  onConfirm={() => void confirmRemoveAll()}
/>

<ConfirmDialog
  open={restoreContent !== null}
  onOpenChange={(next: boolean) => {
    if (!next && !restoring) restoreContent = null;
  }}
  title={i18n.t('personas.restoreTitle')}
  description={i18n.t('personas.restoreConfirm')}
  confirmLabel={i18n.t('common.restore')}
  busy={restoring}
  onConfirm={() => void confirmRestore()}
/>

<style>
  .persona-stack {
    display: grid;
    gap: var(--space-300);
  }

  .persona-list {
    display: grid;
    gap: var(--space-200);
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .persona-list li {
    align-items: center;
    display: flex;
    gap: var(--space-300);
  }

  .persona-copy {
    display: grid;
    flex: 1;
    min-width: 0;
  }

  .persona-name {
    font-weight: var(--font-weight-medium);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .persona-meta {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .persona-empty {
    color: var(--surface-var-on-container);
    margin: 0;
  }

  .persona-actions {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-200);
    justify-content: flex-end;
  }

  .import-form {
    gap: var(--space-300);
  }

  .field {
    display: grid;
    gap: var(--space-200);
  }

  .field span {
    font-weight: var(--font-weight-medium);
  }

  .field small {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
  }

  .field div {
    align-items: center;
    display: flex;
    flex-flow: row wrap;
    justify-content: space-between;
  }
</style>
